import React from "react";
import {
  Modal,
  Typography,
  CircularProgress,
  IconButton,
  Divider,
} from "@mui/material";
import { FaTimes, FaUserMd, FaExchangeAlt, FaCalendarAlt, FaUser } from "react-icons/fa";
import { IDentistHistoryEntry } from "shared/types/DentistHistory";
import { getDentistHistory } from "requests/Patient";
import { useAuth } from "shared/providers/auth";
import * as Styled from "./styles";

interface DentistHistoryModalProps {
  open: boolean;
  onClose: () => void;
  patientId: number;
  patientName: string;
}

const DentistHistoryModal: React.FC<DentistHistoryModalProps> = ({
  open,
  onClose,
  patientId,
  patientName,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [history, setHistory] = React.useState<IDentistHistoryEntry[]>([]);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open && patientId) {
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, patientId]);

  const fetchHistory = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, status } = await getDentistHistory(user.data.id, patientId);

      if ([200, 201].includes(status)) {
        setHistory(data.data);
      } else {
        setError("Erro ao carregar histórico de dentistas.");
      }
    } catch (err) {
      console.error("Erro ao buscar histórico:", err);
      setError("Erro ao carregar histórico de dentistas.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Atual";

    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Styled.ModalContainer>
        <Styled.Header>
          <Styled.HeaderTitle>
            <FaUserMd size={24} />
            <div>
              <Typography variant="h6" fontWeight="bold">
                Histórico de Dentistas
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {patientName}
              </Typography>
            </div>
          </Styled.HeaderTitle>
          <IconButton onClick={onClose} size="small">
            <FaTimes />
          </IconButton>
        </Styled.Header>

        <Divider />

        <Styled.Content>
          {loading ? (
            <Styled.LoadingContainer>
              <CircularProgress />
              <Typography variant="body2" color="textSecondary" mt={2}>
                Carregando histórico...
              </Typography>
            </Styled.LoadingContainer>
          ) : error ? (
            <Styled.EmptyState>
              <Typography color="error">{error}</Typography>
            </Styled.EmptyState>
          ) : history.length === 0 ? (
            <Styled.EmptyState>
              <FaUserMd size={48} color="#ccc" />
              <Typography variant="body1" color="textSecondary" mt={2}>
                Nenhum histórico de dentistas encontrado.
              </Typography>
            </Styled.EmptyState>
          ) : (
            <Styled.Timeline>
              {history.map((entry, index) => (
                <Styled.TimelineItem key={entry.id} isCurrent={entry.is_current}>
                  <Styled.TimelineMarker isCurrent={entry.is_current}>
                    {entry.is_current ? (
                      <FaUserMd size={16} />
                    ) : (
                      <FaExchangeAlt size={14} />
                    )}
                  </Styled.TimelineMarker>

                  <Styled.TimelineContent>
                    <Styled.DentistCard isCurrent={entry.is_current}>
                      {entry.is_first && (
                        <Styled.Badge type="first">Primeiro dentista</Styled.Badge>
                      )}
                      {entry.is_current && (
                        <Styled.Badge type="current">Responsável atual</Styled.Badge>
                      )}

                      <Styled.DentistName>
                        <FaUserMd />
                        {entry.dentist_name}
                      </Styled.DentistName>

                      <Styled.InfoRow>
                        <FaCalendarAlt size={12} />
                        <span>
                          <strong>Início:</strong> {formatDate(entry.start_date)}
                        </span>
                      </Styled.InfoRow>

                      {entry.end_date && (
                        <Styled.InfoRow>
                          <FaCalendarAlt size={12} />
                          <span>
                            <strong>Fim:</strong> {formatDate(entry.end_date)}
                          </span>
                        </Styled.InfoRow>
                      )}

                      {!entry.is_first && entry.dentist_old_name && (
                        <Styled.ChangeInfo>
                          <FaExchangeAlt size={12} />
                          <span>
                            Substituiu: <strong>{entry.dentist_old_name}</strong>
                          </span>
                        </Styled.ChangeInfo>
                      )}

                      <Styled.ChangedBy>
                        <FaUser size={10} />
                        <span>Alterado por: {entry.changed_by}</span>
                      </Styled.ChangedBy>
                    </Styled.DentistCard>
                  </Styled.TimelineContent>
                </Styled.TimelineItem>
              ))}
            </Styled.Timeline>
          )}
        </Styled.Content>
      </Styled.ModalContainer>
    </Modal>
  );
};

export { DentistHistoryModal };















